import React, { useMemo } from 'react';
import { clamp } from 'lodash';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getResultsByFactor } from '@shared/helpers/results.helpers';
import { ConversionFactorUnit, Results } from '@shared/models/unit.model';
import { PRODUCT_CO2_BAR_HEIGHT } from '@ui/ElementList';
import {
  useProposalColor,
  useSelectedProposal,
} from '@features/proposals/use-proposals.hooks';
import {
  useCo2eChartData,
  useCostChartData,
  useFilterResultsBySelectedLifecycles,
} from '@features/results/use-results.hooks';
import ResultsBarCharts from '@features/results/ResultsBarCharts';
import {
  useProductListItemResultsDivisor,
  useProductListMaxResults,
  useProductListEmissionsPerMass as useShowProductListEmissionsPerMass,
} from '../../use-product-list.hooks';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';

interface ProductListItemBarChartsProps {
  results?: Results;
  unit: ConversionFactorUnit;
}

const ProductListItemBarCharts: React.FC<ProductListItemBarChartsProps> = ({
  results = {},
  unit,
}) => {
  const getDivisor = useProductListItemResultsDivisor();
  const lifecycleFilter = useFilterResultsBySelectedLifecycles();

  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();

  const proposal = useSelectedProposal();
  const proposalColor = useProposalColor(proposal?.id ?? '');

  const isPerUnit = useShowProductListEmissionsPerMass();
  const divisor = getDivisor(unit, results);

  const maxResults = useProductListMaxResults();
  const maxResultsFactors = useProductListMaxResults(true);

  const resultsFactors = useMemo(
    () => getResultsByFactor(lifecycleFilter(results), divisor),
    [results, divisor, lifecycleFilter],
  );

  const co2ePerGFAResults = useCo2eChartData(resultsFactors);
  const sekPerGFAResults = useCostChartData(resultsFactors);

  const co2eMax = getConversionFactorValue(maxResultsFactors, 'co2e');
  const sekCostMax = getConversionFactorValue(maxResultsFactors, 'sek');

  const heightScale = getHeightScale(results, maxResults, unit);

  return (
    <ResultsBarCharts
      color={proposalColor}
      co2eHeight={isPerUnit ? heightScale * PRODUCT_CO2_BAR_HEIGHT : undefined}
      co2e={co2ePerGFAResults}
      cost={sekPerGFAResults}
      co2eMax={co2eMax}
      costMax={sekCostMax}
      disableCo2e={co2eVisible.isDisabled}
      disableCost={displayCurrency.isDisabled}
    />
  );
};

const getHeightScale = (
  results: Results,
  maxResults: Results,
  unit: ConversionFactorUnit,
) => {
  const unitValue = results[unit] ?? 1;
  const maxUnitValue = maxResults[unit] ?? 1;
  return clamp(unitValue / maxUnitValue, 0.08, 1);
};

export default ProductListItemBarCharts;
